import { useState, useEffect, useMemo } from 'react';
import { gradingApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Sliders, GraduationCap, PencilSimple, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PERIOD_TYPES = [
  { value: 'periode', label: 'Période (1ère à 6ème + 3 examens)' },
  { value: 'trimestre', label: 'Trimestre (1er, 2è, 3è)' },
  { value: 'semestre', label: 'Semestre (1er, 2è)' },
];

const TEMPLATE_BADGES = {
  maternelle: 'bg-pink-50 text-pink-700 border-pink-200',
  primaire_gs_cp: 'bg-amber-50 text-amber-700 border-amber-200',
  primaire_ce1_cm2: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  secondaire: 'bg-blue-50 text-blue-700 border-blue-200',
};

export default function BulletinsConfigTab({ activeSchoolYear }) {
  const [templates, setTemplates] = useState([]);
  const [cycleCfg, setCycleCfg] = useState({ maternelle: 'periode', primaire: 'trimestre', secondaire: 'trimestre' });
  const [classes, setClasses] = useState([]);
  const [editingCoef, setEditingCoef] = useState(null); // class_name being edited
  const [coefData, setCoefData] = useState(null);
  const [coefSaving, setCoefSaving] = useState(false);
  const [savingCycle, setSavingCycle] = useState(false);

  useEffect(() => { (async () => {
    try {
      const [t, c, list] = await Promise.all([
        gradingApi.templates(),
        gradingApi.cyclePeriodConfig(),
        gradingApi.classConfigs(activeSchoolYear),
      ]);
      setTemplates(t.data.templates);
      setCycleCfg(c.data);
      setClasses(list.data);
    } catch (e) {
      toast.error('Erreur chargement config bulletins');
    }
  })(); }, [activeSchoolYear]);

  const templatesByKey = useMemo(
    () => Object.fromEntries((templates || []).map(t => [t.key, t])),
    [templates],
  );

  const saveCycleCfg = async () => {
    setSavingCycle(true);
    try {
      await gradingApi.saveCyclePeriodConfig(cycleCfg);
      toast.success('Périodes par cycle enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSavingCycle(false); }
  };

  const updateClassTemplate = async (cls, template_key) => {
    try {
      await gradingApi.saveClassConfig({
        class_name: cls.class_name, school_year: cls.school_year,
        template_key, period_type: cls.period_type,
      });
      toast.success(`${cls.class_name} → ${templatesByKey[template_key]?.label}`);
      const { data } = await gradingApi.classConfigs(activeSchoolYear);
      setClasses(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const updateClassPeriod = async (cls, period_type) => {
    try {
      await gradingApi.saveClassConfig({
        class_name: cls.class_name, school_year: cls.school_year,
        template_key: cls.template_key, period_type,
      });
      toast.success(`${cls.class_name} → ${period_type}`);
      const { data } = await gradingApi.classConfigs(activeSchoolYear);
      setClasses(data);
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const openCoefEditor = async (cls) => {
    try {
      const { data } = await gradingApi.classCoefficients(cls.class_name, cls.school_year);
      setCoefData(data);
      setEditingCoef(cls.class_name);
    } catch (e) {
      toast.error('Erreur chargement coefficients');
    }
  };

  const setCoef = (subject, value) => {
    setCoefData(prev => ({
      ...prev,
      overrides: { ...prev.overrides, [subject]: value === '' ? '' : Number(value) },
    }));
  };

  const saveCoefs = async () => {
    if (!coefData) return;
    setCoefSaving(true);
    try {
      const cleaned = {};
      Object.entries(coefData.overrides || {}).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v))) cleaned[k] = Number(v);
      });
      await gradingApi.saveClassCoefficients({
        class_name: coefData.class_name,
        school_year: coefData.school_year,
        coefficients: cleaned,
      });
      toast.success('Coefficients sauvegardés');
      setEditingCoef(null);
      setCoefData(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setCoefSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="bulletins-config-tab">
      {/* Period by cycle */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <Sliders size={18} className="text-[#0052FF]" weight="duotone" />
            Découpage de l'année scolaire par cycle
          </CardTitle>
          <p className="text-xs text-[#64748B] mt-1">
            Chaque cycle peut suivre son propre rythme (période, trimestre ou semestre).
            Vous pouvez ensuite forcer un rythme spécifique pour une classe donnée plus bas.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: 'maternelle', label: 'Maternelle' },
              { key: 'primaire', label: 'Primaire' },
              { key: 'secondaire', label: 'Secondaire' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <Label className="font-manrope">{label}</Label>
                <Select
                  value={cycleCfg[key]}
                  onValueChange={(v) => setCycleCfg({ ...cycleCfg, [key]: v })}
                >
                  <SelectTrigger className="border-[#E2E8F0]" data-testid={`cycle-period-${key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIOD_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={saveCycleCfg} disabled={savingCycle} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-cycle-cfg">
              <FloppyDisk size={16} className="mr-2" />
              {savingCycle ? '…' : 'Enregistrer'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Class → template mapping */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <GraduationCap size={18} className="text-emerald-600" weight="duotone" />
            Modèle de bulletin par classe
          </CardTitle>
          <p className="text-xs text-[#64748B] mt-1">
            Auto-détecté depuis le nom de la classe. Cliquez sur « Coefficients » pour personnaliser
            les pondérations (Collège/Lycée).
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Classe</TableHead>
                <TableHead>Modèle</TableHead>
                <TableHead>Rythme</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {classes.map((c) => (
                <TableRow key={c.class_name} data-testid={`class-config-${c.class_name}`}>
                  <TableCell className="font-manrope font-semibold">
                    {c.class_name}
                    {c._autodetected && <span className="ml-2 text-[10px] uppercase text-[#94A3B8]">auto</span>}
                  </TableCell>
                  <TableCell>
                    <Select value={c.template_key} onValueChange={(v) => updateClassTemplate(c, v)}>
                      <SelectTrigger className={`border h-9 text-xs w-56 ${TEMPLATE_BADGES[c.template_key] || ''}`}
                        data-testid={`class-template-${c.class_name}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={c.period_type} onValueChange={(v) => updateClassPeriod(c, v)}>
                      <SelectTrigger className="border-[#E2E8F0] h-9 text-xs w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIOD_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    {c.template_key === 'secondaire' && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => openCoefEditor(c)}
                        className="text-xs"
                        data-testid={`open-coefs-${c.class_name}`}
                      >
                        <PencilSimple size={14} className="mr-1" />
                        Coefficients
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Coefficient editor */}
      {editingCoef && coefData && (
        <Card className="border-2 border-emerald-300 shadow-md" data-testid="coef-editor">
          <CardHeader className="pb-3 bg-emerald-50/40">
            <CardTitle className="font-outfit text-base">
              Coefficients — {coefData.class_name}
            </CardTitle>
            <p className="text-xs text-[#64748B] mt-1">
              Laisser vide pour conserver la valeur par défaut du modèle.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {coefData.subjects.map((s) => {
                const overrideVal = coefData.overrides?.[s.name];
                const placeholder = String(coefData.default_coefficients?.[s.name] ?? 1);
                return (
                  <div key={s.name} className="flex items-center gap-2">
                    <Label className="text-xs flex-1 truncate">{s.name}</Label>
                    <Input
                      type="number" min="0" step="0.5"
                      value={overrideVal === undefined || overrideVal === null ? '' : overrideVal}
                      placeholder={placeholder}
                      onChange={(e) => setCoef(s.name, e.target.value)}
                      className="w-20 h-9 text-center"
                      data-testid={`coef-input-${s.name}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditingCoef(null); setCoefData(null); }}>Annuler</Button>
              <Button onClick={saveCoefs} disabled={coefSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-coefs">
                <FloppyDisk size={16} className="mr-2" />
                {coefSaving ? '…' : 'Enregistrer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates reference (read-only) */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base">Aperçu des 4 modèles</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="maternelle">
            <TabsList className="bg-[#F1F5F9]">
              {templates.map(t => (
                <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-white text-xs">
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {templates.map(t => (
              <TabsContent key={t.key} value={t.key} className="mt-4">
                <p className="text-xs text-[#64748B] mb-3">
                  Mode : <code className="bg-slate-100 px-1.5 py-0.5 rounded">{t.grading_mode}</code>
                  {t.total_max ? ` · Total /${t.total_max}` : ''}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {t.subjects.map((s, i) => (
                    <div key={i} className="text-xs border border-[#E2E8F0] rounded p-2">
                      <p className="font-semibold text-[#0F172A]">
                        {s.name}
                        {s.max_score ? ` (/${s.max_score})` : ''}
                        {s.default_coef ? ` · coef ${s.default_coef}` : ''}
                      </p>
                      {Array.isArray(s.sub_activities) && s.sub_activities.length > 0 && (
                        <ul className="mt-1 ml-3 list-disc text-[#64748B]">
                          {s.sub_activities.map((sa, j) => (
                            <li key={j}>
                              {typeof sa === 'string' ? sa : `${sa.name} (/${sa.max_score})`}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
